import { IconTypes } from '@genial/components';
import { IconographyTypes } from '@genial/icons';
import {
  CustomerResponsesAdditionalAccountsSection,
  ResponsesCustomerAccountsResponse,
  ResponsesCustomerFinancialV3Response,
  ResponsesCustomerFinancialV4Response,
} from '@genial/services/dist/models';
import { Utils } from '@genial/utils';

import {
  CustomerGlobalSearchResponseNormalized,
  CustomerV3FinancialResponseNormalized,
  CustomerV4FinancialResponseNormalized,
} from './types';

const errorMessage = '--';

export const normalizeInitials = (fullName: string | null | undefined): string => {
  if (!fullName) {
    return '';
  }
  const names = fullName.trim().split(' ');
  const initials =
    names.length === 1
      ? names[0].charAt(0) + names[0].charAt(1)
      : names[0].charAt(0) + names[names.length - 1].charAt(0);
  return initials.toUpperCase();
};

export const mountAccount = (
  accountNumber: number,
  accountDigit: number | string | null | undefined
): string => {
  const checkEmptyResult = (account: string): string => {
    const emptyResults = ['null', 'undefined', '0', '0-0'];
    if (emptyResults.includes(account)) {
      return 'Não encontrada';
    }
    return account;
  };

  const normalizeAccount = (): string => {
    if (accountDigit === null || accountDigit === undefined) {
      return `${accountNumber}`;
    }
    return `${accountNumber}-${accountDigit}`;
  };

  return checkEmptyResult(normalizeAccount());
};

export const normalizeV3Accounts = <
  T extends CustomerResponsesAdditionalAccountsSection | ResponsesCustomerAccountsResponse
>(
  accounts: T[] | null | undefined
): (T & { sinacorAccountNormalized: string; custodyAccountNormalized: string })[] => {
  if (!accounts) {
    return [];
  }

  return accounts.map((account) => ({
    ...account,
    sinacorAccountNormalized: mountAccount(account.accountNumber, account.accountDigit),
    custodyAccountNormalized: mountAccount(account.pnpAccountNumber, account.pnpAccountDigit),
  }));
};

export const normalizeV3Clients = (
  customers: ResponsesCustomerFinancialV3Response[]
): CustomerV3FinancialResponseNormalized[] => {
  return customers.map((customer) => {
    const statusNormalized = customer.status.statusName?.toUpperCase() ?? '--';
    return {
      initialsNormalized: normalizeInitials(customer.fullName),
      customerNameNormalized: customer.fullName ?? errorMessage,
      cpfCnpjNormalized: customer.cpfCnpjText
        ? Utils.Mask.CpfOrCnpj(customer.cpfCnpjText)
        : errorMessage,
      personTypeNormalized: customer.personTypeName ?? errorMessage,
      netAmount: 0,
      netAmountUpdatedAt: new Date(),
      accountDateNormalized: Utils.Mask.DateFormat(customer.createdAt, 'dd/MM/yyyy'),
      suitabilityLabelNormalized:
        customer.suitabilityProfileAcronym || customer.suitabilityProfileName || '',
      suitabilityColorNormalized: Utils.Suitability.getCorrectColor(
        customer.suitabilityProfileName || ''
      ),
      accountStatusNormalized: statusNormalized,
      accountStatusTextColorNormalized:
        statusNormalized !== 'REGULAR'
          ? Utils.ColorToken.fromColor(customer.status.iconColor)
          : 'neutral.black',
      accountStatusIconColorNormalized: Utils.ColorToken.fromColor(customer.status.iconColor),
      accountStatusIconNormalized: (customer.status.icon ?? 'Info') as IconographyTypes.Option,
      accountStatusIconSizeNormalized: (customer.status.iconSize as IconTypes.Sizes) ?? 'xxxs',
      accountsNormalized: normalizeV3Accounts(customer.customerAccounts),
      ...customer,
    };
  });
};

export const normalizeV4Clients = (
  customers: ResponsesCustomerFinancialV4Response[]
): CustomerV4FinancialResponseNormalized[] => {
  return customers.map((customer) => {
    const statusNormalized = customer.status.statusName?.toUpperCase() ?? '--';
    return {
      initialsNormalized: normalizeInitials(customer.fullName),
      customerNameNormalized: customer.fullName ?? errorMessage,
      cpfCnpjNormalized: customer.cpfCnpjText
        ? Utils.Mask.CpfOrCnpj(customer.cpfCnpjText)
        : errorMessage,
      personTypeNormalized: customer.personTypeName ?? errorMessage,
      netAmount: 0,
      netAmountUpdatedAt: new Date(),
      accountDateNormalized: Utils.Mask.DateFormat(customer.createdAt, 'dd/MM/yyyy'),
      suitabilityLabelNormalized:
        customer.suitabilityProfileAcronym || customer.suitabilityProfileName || '',
      suitabilityColorNormalized: Utils.Suitability.getCorrectColor(
        customer.suitabilityProfileName || ''
      ),
      accountStatusNormalized: statusNormalized,
      accountStatusTextColorNormalized:
        statusNormalized !== 'REGULAR'
          ? Utils.ColorToken.fromColor(customer.status.iconColor)
          : 'neutral.black',
      accountStatusIconColorNormalized: Utils.ColorToken.fromColor(customer.status.iconColor),
      accountStatusIconNormalized: (customer.status.icon ?? 'Info') as IconographyTypes.Option,
      accountStatusIconSizeNormalized: (customer.status.iconSize as IconTypes.Sizes) ?? 'xxxs',
      accountsNormalized: normalizeV3Accounts(customer.customerAccounts),
      ...customer,
    };
  });
};

export const normalizeV4ClientsUngrouped = (
  customers: CustomerV4FinancialResponseNormalized[]
): CustomerGlobalSearchResponseNormalized[] => {
  const ungrouped: CustomerGlobalSearchResponseNormalized[] = [];

  customers.forEach((customer) => {
    customer.accountsNormalized?.forEach((account) => {
      ungrouped.push({
        accountId: account.accountId,
        accountNumber: account.accountNumber,
        pnpAccountNumber: account.pnpAccountNumber,
        cpfCnpjNormalized: customer.cpfCnpjNormalized,
        customerNameNormalized: customer.customerNameNormalized,
        sinacorAccountNormalized: account.sinacorAccountNormalized,
        custodyAccountNormalized: account.custodyAccountNormalized,
      });
    });
  });

  return ungrouped;
};
